/* eslint-disable @typescript-eslint/no-empty-function */
/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable max-lines-per-function */
/* eslint-disable camelcase */
import React, { createContext, useContext, useMemo, useState } from "react";

export type TableResult = {
  id: string;
  applicant_name: string;
  status: string;
  has_star: boolean;
  interest_list_id: string;
}

type PageRangeProps = {
  from: number | null,
  to: number | null,
  totalPages: number,
}
type SelectPageRangeProps = PageRangeProps & {visibleIds: string[]};

// Define the shape of the context state
const InterestContext = createContext({
  selectedPageRange: { from: null, to: null, totalPages: 0 } as PageRangeProps,
  selectedInterestIds: [] as string[],
  totalTableResults: 0,
  tableResults: [] as TableResult[],
  hasSelectedAllInterests: false,
  totalSelectedInterests: 0,
  selectedSomeInterests: false,
  fullPageOrAllPagesSelected: false,
  toggleSelectionId: (_id: string, _checked: boolean) => {},
  selectAllInterests: (_visibleIds: string[]) => {},
  deselectAllInterests: () => {},
  replaceAllSelection: (_ids: string[]) => {},
  setTotalTableResults: (_count: number) => {},
  setTableResults: (_results: TableResult[]) => {},
  selectPageRange: (_range: SelectPageRangeProps) => {},
});


const PAGE_SIZE = 25;

// Provider component
export const InterestsTableProvider: React.FC = ({ children }) => {
  const [selectedInterestIds, setSelectedInterestIds] = useState<string[]>([]);
  const [totalTableResults, setTotalTableResults] = useState(0);
  const [tableResults, setTableResults] = useState<TableResult[]>([]);
  const [hasSelectedAllInterests, setHasSelectedAllInterests] = useState(false);
  const [selectedPageRange, setSelectedPageRange] =
    useState<PageRangeProps>({ from: null, to: null, totalPages: 0 });

  const totalSelectedInterests = useMemo(() => {
    if (selectedPageRange.from !== null || selectedPageRange.to !== null) {
      if (selectedPageRange.to === (selectedPageRange.totalPages - 1)) {
        return totalTableResults - (PAGE_SIZE * (selectedPageRange.from || 0));
      }
      const minPage = (selectedPageRange.from || 0);
      const maxPage = 1 + (selectedPageRange.to === null ? (
        selectedPageRange.totalPages - 1) : selectedPageRange.to);
      return PAGE_SIZE * (maxPage - minPage);
    }

    if (hasSelectedAllInterests) return totalTableResults;
    return selectedInterestIds.length;
  }, [hasSelectedAllInterests, selectedInterestIds.length, totalTableResults, selectedPageRange]);

  const selectedSomeInterests = useMemo(
    () =>
      !hasSelectedAllInterests && selectedInterestIds.length > 0,
    [hasSelectedAllInterests, selectedInterestIds.length],
  );

  const fullPageOrAllPagesSelected = useMemo(() => {
    if (totalSelectedInterests === 0 || tableResults.length === 0) return false;
    return hasSelectedAllInterests || totalSelectedInterests === tableResults.length;
  }, [totalSelectedInterests, tableResults.length, hasSelectedAllInterests]);

  const selectPageRange = ({ from, to, totalPages, visibleIds }: SelectPageRangeProps) => {
    setSelectedPageRange({ from, to, totalPages });
    setSelectedInterestIds(visibleIds);
    setHasSelectedAllInterests(false);
  };

  // Actions similar to your MobX actions
  const toggleSelectionId = (id: string, checked: boolean) => {
    if (checked) {
      setSelectedInterestIds(prevIds => [...prevIds, id]);
    } else {
      setSelectedInterestIds(prevIds => prevIds.filter(item => item !== id));
    }
  };

  const selectAllInterests = (visibleIds?: string[]) => {
    setHasSelectedAllInterests(true);
    setSelectedInterestIds(visibleIds || []);
    setSelectedPageRange({ from: null, to: null, totalPages: 0 });
  };

  const deselectAllInterests = () => {
    setHasSelectedAllInterests(false);
    setSelectedPageRange({ from: null, to: null, totalPages: 0 });
    setSelectedInterestIds([]);
  };

  const replaceAllSelection = (ids: string[]) => {
    setHasSelectedAllInterests(false);
    setSelectedPageRange({ from: null, to: null, totalPages: 0 });
    setSelectedInterestIds(ids);
  };

  return (
    <InterestContext.Provider value={{
      selectedPageRange,
      selectPageRange,
      selectedInterestIds,
      totalTableResults,
      tableResults,
      hasSelectedAllInterests,
      toggleSelectionId,
      selectAllInterests,
      deselectAllInterests,
      replaceAllSelection,
      setTotalTableResults,
      setTableResults,
      totalSelectedInterests,
      selectedSomeInterests,
      fullPageOrAllPagesSelected,
    }}>
      {children}
    </InterestContext.Provider>
  );
};

// Custom hook to use the context
export const useInterestsTable = () => useContext(InterestContext);
